import { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Slider,
  message,
  Spin,
  Radio,
  Modal,
  Switch,
  Button,
  Typography,
} from "antd";
import { IoWater, IoNotifications } from "react-icons/io5";
import { FaGlassWaterDroplet } from "react-icons/fa6";
import { MdHourglassFull } from "react-icons/md";
import { FaCloudSun } from "react-icons/fa";
import { rtdb } from "../firebase";
import { ref, onValue, update } from "firebase/database";
import { GrGrow } from "react-icons/gr";
import SliderTitle from "../components/SliderTitle";
import { useAuth } from "../context/AuthContext";
import { requestNotificationToken } from "../firebase-messaging";
const { Text } = Typography;

const optionsSaisons = [
  { label: "Croissance", value: "growing" },
  { label: "Pré-floraison", value: "preflowering" },
  { label: "Floraison", value: "flowering" },
];

const Parametres = () => {
  const { selectedGreenhouse, currentUser, hasPushToken, setPushTokenPresent } = useAuth();
  const [params, setParams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalHOpen, setIsModalHOpen] = useState({ show: false, h: null });
  const [notifLoading, setNotifLoading] = useState(false);
  const [sliderValues, setSliderValues] = useState({});

  useEffect(() => {
    if (!selectedGreenhouse) {
      return;
    }

    const paramsRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/params`);

    const unsubscribe = onValue(
      paramsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setParams(data);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [selectedGreenhouse]);

  const updateData = useCallback(async (path, value) => {
    try {
      if (!selectedGreenhouse) {
        message.error("Veuillez sélectionner une serre");
        return;
      }

      const updates = {};
      updates[`greenhouses/${selectedGreenhouse}/params/${path}`] = value;
      await update(ref(rtdb), updates);
      message.success({
        content: "Mise à jour réussie",
        key: "upd",
        duration: 1,
      });
    } catch {
      message.error("Erreur lors de la sauvegarde");
    }
  }, [selectedGreenhouse]);

  const handleEnableNotif = async () => {
    if (!currentUser) {
      message.error("Vous devez être connecté");
      return;
    }
    setNotifLoading(true);
    try {
      const token = await requestNotificationToken();
      const { set } = await import("firebase/database");
      await set(
        ref(rtdb, `pushTokens/${currentUser.uid}/${token}`),
        {
          greenhouseId: selectedGreenhouse,
          createdAt: new Date().toISOString(),
          userAgent: window.navigator.userAgent,
        },
      );
      setPushTokenPresent(true);
      message.success({ content: "Notifications activées !", key: "notif", duration: 2 });
    } catch (err) {
      message.error("Erreur : " + err.message);
    }
    setNotifLoading(false);
  };

  const handleFacultativeHourToggle = useCallback((hour, force = false) => {
    if (!params?.irrigation) return;

    let newFacultativeHours = params.irrigation.facultativeHours
      ? [...params.irrigation.facultativeHours]
      : [];
    if (newFacultativeHours.includes(hour) || force) {
      newFacultativeHours = newFacultativeHours.filter((h) => h !== hour);
    } else {
      newFacultativeHours.sort((a, b) => a - b);
      newFacultativeHours.push(hour);
      newFacultativeHours.sort((a, b) => a - b);
    }

    updateData("irrigation/facultativeHours", newFacultativeHours);
  }, [params?.irrigation, updateData]);

  const handleHourToggle = useCallback((hour) => {
    if (!params?.irrigation) return;

    let newHours = params.irrigation.hours ? [...params.irrigation.hours] : [];
    if (newHours.includes(hour)) {
      newHours = newHours.filter((h) => h !== hour);
      handleFacultativeHourToggle(hour, true);
    } else {
      newHours.sort((a, b) => a - b);
      newHours.push(hour);
      newHours.sort((a, b) => a - b);
    }

    updateData("irrigation/hours", newHours);
  }, [params?.irrigation, updateData, handleFacultativeHourToggle]);

  if (loading)
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  if (!params) return <p>Aucune donnée trouvée.</p>;

  const renderHourButtons = () => {
    return Array.from({ length: 24 }).map((_, hour) => {
      const isSelected = params.irrigation.hours?.includes(hour);
      const isFacultative = isSelected
        ? params.irrigation.facultativeHours?.includes(hour)
        : false;

      return (
        <Col span={4} key={hour} style={{ marginBottom: "4px" }}>
          <button
            onClick={() =>
              isSelected
                ? setIsModalHOpen({ show: true, h: hour })
                : handleHourToggle(hour)
            }
            className="hour-button"
            style={{
              background: isSelected
                ? isFacultative
                  ? "var(--color-geekblue)"
                  : "var(--color-primary)"
                : "var(--bg-card-header)",
            }}
          >
            {hour}h
          </button>
        </Col>
      );
    });
  };

  return (
    <div className="page-container">
      <h2 className="page-title">Paramètres de Culture</h2>

      <Row gutter={[16, 16]} className="row-gutter-top">
        <Col span={24}>
          <Card
            title={
              <div className="card-title-flex">
                <GrGrow className="card-title-icon" style={{ color: "#42b72aff" }} />
                Etapes de croissance
              </div>
            }
            className="card-style"
            classNames={{ header: "card-header-style", body: "card-body-style" }}
          >
            <Radio.Group
              options={optionsSaisons}
              value={params.currentPeriod || "growing"}
              optionType="button"
              buttonStyle="solid"
              onChange={(e) => updateData("currentPeriod", e.target.value)}
            />
            <div className="description">
              <Text type="secondary" size="small">
                Ce sont les trois grandes phases de croissance des plantes :
                globalement le début du printemps, le début de l'été et le plein
                été.
              </Text>
            </div>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <div className="card-title-flex">
                <IoWater className="card-title-icon" style={{ color: "#4c81ccff" }} />
                Irrigation
              </div>
            }
            className="card-style"
            classNames={{ header: "card-header-style", body: "card-body-style" }}
          >
            <h4>Heures d'arrosage</h4>
            <Row gutter={[4, 4]} style={{ marginBottom: "20px" }}>
              {renderHourButtons()}
            </Row>

            <SliderTitle
              title="Durée d'irrigation"
              data={sliderValues.duration_min ?? params.irrigation.duration_min}
              color="#1677ff"
              unit="min"
            />

            <Slider
              min={1}
              max={15}
              tooltip={{ formatter: null }}
              value={sliderValues.duration_min ?? params.irrigation.duration_min}
              onChange={(val) => setSliderValues(prev => ({ ...prev, duration_min: val }))}
              onChangeComplete={(val) => {
                updateData("irrigation/duration_min", val);
                setSliderValues(prev => ({ ...prev, duration_min: undefined }));
              }}
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <div className="card-title-flex">
                <MdHourglassFull className="card-title-icon" style={{ color: "#d58a1aff" }} />
                Time Out de Remplissage
              </div>
            }
            className="card-style"
            classNames={{ header: "card-header-style", body: "card-body-style" }}
          >
            <SliderTitle
              title="Temps de sécurité"
              data={sliderValues.max_fill_time ?? params.irrigation.max_fill_time}
              color="#eb920eff"
              colorTitle="#eb920eff"
              unit="min"
            />

            <Slider
              min={1}
              max={20}
              tooltip={{ formatter: null }}
              value={sliderValues.max_fill_time ?? params.irrigation.max_fill_time}
              onChange={(val) => setSliderValues(prev => ({ ...prev, max_fill_time: val }))}
              onChangeComplete={(val) => {
                updateData("irrigation/max_fill_time", val);
                setSliderValues(prev => ({ ...prev, max_fill_time: undefined }));
              }}
            />
            <div className="description">
              <Text type="secondary" size="small">
                Le remplissage s'arrête automatiquement lorsque la sonde détecte
                le niveau haut ; toutefois, par mesure de sécurité, une durée
                maximale est paramétrée.
              </Text>
            </div>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <div className="card-title-flex">
                <FaCloudSun className="card-title-icon" style={{ color: "#bab4a4ff" }} />
                Seuils climatiques
              </div>
            }
            className="card-style"
            classNames={{ header: "card-header-style", body: "card-body-style" }}
          >
            <SliderTitle
              title="Seuil Humidité"
              data={sliderValues.humidity_threshold ?? params.irrigation.humidity_threshold}
              color="#1677ff"
              unit="%"
            />

            <Slider
              min={10}
              max={100}
              tooltip={{ formatter: null }}
              value={sliderValues.humidity_threshold ?? params.irrigation.humidity_threshold}
              onChange={(val) => setSliderValues(prev => ({ ...prev, humidity_threshold: val }))}
              onChangeComplete={(val) => {
                updateData("irrigation/humidity_threshold", val);
                setSliderValues(prev => ({ ...prev, humidity_threshold: undefined }));
              }}
            />

            <SliderTitle
              title="Seuil Température"
              data={sliderValues.temp_threshold ?? params.irrigation.temp_threshold}
              color="#1677ff"
              unit="°C"
            />

            <Slider
              min={10}
              max={40}
              tooltip={{ formatter: null }}
              value={sliderValues.temp_threshold ?? params.irrigation.temp_threshold}
              onChange={(val) => setSliderValues(prev => ({ ...prev, temp_threshold: val }))}
              onChangeComplete={(val) => {
                updateData("irrigation/temp_threshold", val);
                setSliderValues(prev => ({ ...prev, temp_threshold: undefined }));
              }}
            />
            <div className="description">
              <Text type="secondary" size="small">
                Les heures d'irrigation facultatives ne se déclenchent que si la
                température dépasse le seuil défini et si le taux d'humidité est
                inférieur au seuil d'humidité configuré.
              </Text>
            </div>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <div className="card-title-flex">
                <FaGlassWaterDroplet className="card-title-icon" style={{ color: "#3ca33cff" }} />
                Réservoir
              </div>
            }
            className="card-style"
            classNames={{ header: "card-header-style", body: "card-body-style" }}
          >
            <SliderTitle
              title="Capacité du réservoir"
              data={sliderValues.capacity ?? params.reservoir.capacity}
              color="#3ca33cff"
              colorTitle="#3ca33cff"
              unit="L"
            />
            <Slider
              min={10}
              max={100}
              tooltip={{ formatter: null }}
              value={sliderValues.capacity ?? params.reservoir.capacity}
              onChange={(val) => setSliderValues(prev => ({ ...prev, capacity: val }))}
              onChangeComplete={(val) => {
                updateData("reservoir/capacity", val);
                setSliderValues(prev => ({ ...prev, capacity: undefined }));
              }}
            />
            <div className="description">
              <Text type="secondary" size="small">
                Capacité totale de votre cuve de nutriments. Les dosages des nutriments sont calculés automatiquement en fonction de cette valeur.
              </Text>
            </div>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <div className="card-title-flex">
                <FaGlassWaterDroplet className="card-title-icon" style={{ color: "#3ca33cff" }} />
                Nutriments
              </div>
            }
            className="card-style"
            classNames={{ header: "card-header-style", body: "card-body-style" }}
          >
            <SliderTitle
              title="Grow A"
              data={Math.round(
                params.nutriment_quantity[params.currentPeriod].A *
                params.reservoir.capacity
              )}
              color="#52a852ff"
              colorTitle="#52a852ff"
              unit="ml"
            />

            <Slider
              min={1}
              max={3 * params.reservoir.capacity}
              value={Math.round(
                params.nutriment_quantity[params.currentPeriod].A *
                params.reservoir.capacity
              )}
            />
            <SliderTitle
              title="Micro B"
              data={Math.round(
                params.nutriment_quantity[params.currentPeriod].B *
                params.reservoir.capacity
              )}
              color="#937951ff"
              colorTitle="#937951ff"
              unit="ml"
            />

            <Slider
              min={1}
              max={3 * params.reservoir.capacity}
              value={Math.round(
                params.nutriment_quantity[params.currentPeriod].B *
                params.reservoir.capacity
              )}
            />
            <SliderTitle
              title="Bloom C"
              data={Math.round(
                params.nutriment_quantity[params.currentPeriod].C *
                params.reservoir.capacity
              )}
              color="#b6539bff"
              colorTitle="#b6539bff"
              unit="ml"
            />

            <Slider
              min={1}
              max={3 * params.reservoir.capacity}
              value={Math.round(
                params.nutriment_quantity[params.currentPeriod].C *
                params.reservoir.capacity
              )}
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <div className="card-title-flex">
                <IoNotifications className="card-title-icon" style={{ color: "#1677ff" }} />
                Notifications
              </div>
            }
            className="card-style"
            classNames={{ header: "card-header-style", body: "card-body-style" }}
          >
            {currentUser && !hasPushToken ? (
              <Button
                type="primary"
                icon={<IoNotifications />}
                loading={notifLoading}
                onClick={handleEnableNotif}
                block
              >
                Activer les notifications
              </Button>
            ) : currentUser && hasPushToken ? (
              <Text className="color-success">
                ✅ Notifications activées
              </Text>
            ) : (
              <Text type="secondary">Connectez-vous pour activer les notifications</Text>
            )}
          </Card>
        </Col>
      </Row>
      <Modal
        title="Modification d'horaire"
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalHOpen.show}
        onOk={() => setIsModalHOpen({ show: false, h: null })}
        onCancel={() => setIsModalHOpen({ show: false, h: null })}
        footer={[
          <Button
            key="back"
            onClick={() => setIsModalHOpen({ show: false, h: null })}
          >
            Fermer
          </Button>,
        ]}
      >
        <div className="modal-card">
          <p style={{ fontSize: 14, color: "var(--text-light)" }}>
            Un horaire facultatif : cette période d'irrigation ne se déclenche
            que si les conditions météo dépassent les seuils paramétrés.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span>Horaire facultatif</span>
            <Switch
              defaultChecked
              checked={params.irrigation.facultativeHours?.includes(isModalHOpen.h)}
              onChange={() => handleFacultativeHourToggle(isModalHOpen.h)}
            />
          </div>
        </div>

        <div className="modal-card">
          <p style={{ fontSize: 14, color: "var(--text-light)" }}>
            Supprimer complètement cette période d'irrigation
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button
              color="danger"
              variant="solid"
              onClick={() => {
                handleHourToggle(isModalHOpen.h);
                setIsModalHOpen({ show: false, h: null });
              }}
            >
              Supprimer cet horaire
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Parametres;
